import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoutes from './router/router';
import { Provider, useSelector, useDispatch } from 'react-redux';
import store from './store/store';  // Import as default
import { loginSuccess, logout } from './store/userSlice';
import { setPrescription } from './store/prescriptionSlice';
import { API_BASE_URL } from './config';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// On load, refresh the logged-in user from the server so account data
// (incl. saved prescription & addresses) is always current. Uses raw axios so
// a stale/expired token doesn't trigger the global redirect-to-login interceptor.
function AuthBootstrap() {
  const dispatch = useDispatch();
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_BASE_URL}/users/getMe`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const user = {
          ...data.data,
          addresses: data.data.addresses || [],
          orders: data.data.orders || [],
        };
        dispatch(loginSuccess({ token, user }));
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          dispatch(logout());
        }
        // transient/offline errors: keep the persisted session as-is
      });
  }, [dispatch]);
  return null;
}

// Auto-applies the logged-in user's saved prescription across the app
// (eye calculator + product power selection), once per user.
function PrescriptionSync() {
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s.user.currentUser);
  const rxIsSet = useSelector((s) => s.prescription.isSet);
  const syncedFor = useRef(null);

  useEffect(() => {
    const rx = currentUser?.prescription;
    const uid = currentUser?._id;
    if (!rx || rxIsSet || syncedFor.current === uid) return;
    const hasVals = [rx.rightEye, rx.leftEye].some(
      (e) => e && (e.sph != null || e.cyl != null || e.axis != null),
    );
    if (hasVals) {
      dispatch(setPrescription({
        rightEye: rx.rightEye || undefined,
        leftEye: rx.leftEye || undefined,
        method: rx.method || 'manual',
      }));
      syncedFor.current = uid;
    }
  }, [currentUser, rxIsSet, dispatch]);

  return null;
}

function App() {
  return (
    <Router>
      <Provider store={store}>
        <AuthBootstrap />
        <PrescriptionSync />
        <AppRoutes />
        <ToastContainer
          position="bottom-right"
          autoClose={2000}
          hideProgressBar
          closeOnClick
          pauseOnHover={false}
          draggable={false}
          newestOnTop
          toastClassName="!rounded-xl !shadow-lg !text-sm !font-medium"
        />
      </Provider>
    </Router>
  );
}

export default App;
